/**
 * Classifier for WebSocket `close` events.
 *
 * Pulled out of `WebSocketClient` so the level/severity decision is
 * pure, easy to enumerate in tests, and can be reused by anyone else who
 * needs to log a CloseEvent.
 *
 * Background: the browser `error` event carries no useful information
 * (`{isTrusted:true}` only) and always fires immediately before an
 * abnormal `close`. The close event is where the actual diagnostics
 * live (`code`, `reason`, `wasClean`). This module decides whether
 * a given close is:
 *  - `silent`: clean, expected (tab close, navigation) — don't log.
 *  - `warn`: transient or server-side blip (1006 abnormal closure,
 *    1011-1014 server restart/overload, clean codes pre-auth) — log,
 *    but don't trip error monitors.
 *  - `error`: protocol violation (we sent something bad, or the server
 *    did) OR sustained outage (N consecutive failed reconnects).
 */

/**
 * Per RFC 6455, these close codes mean the socket carried a malformed
 * or policy-violating frame. They indicate a real bug in our code or in
 * the server, not a network blip, so they go straight to `error`:
 *  - 1002 protocol error
 *  - 1003 unsupported data
 *  - 1007 invalid frame payload data
 *  - 1008 policy violation
 *  - 1009 message too big
 *  - 1010 mandatory extension missing
 *  - 1015 TLS handshake failure
 *
 * 1011-1014 (internal error / restart / overload / gateway) are
 * deliberately *not* in this set: those are noisy server-side hiccups
 * and the sustained-outage escalation surfaces them on its own.
 */
export const PROTOCOL_VIOLATION_CLOSE_CODES = new Set<number>([
  1002, 1003, 1007, 1008, 1009, 1010, 1015,
]);

/**
 * Close codes that indicate a clean, expected closure: 1000 normal,
 * 1001 going away (tab close / navigation), 1005 no status received
 * (most browser-initiated closes look like this). Combined with
 * `wasAuthed`, these go silent: the status listener already surfaced
 * the transition.
 */
export const CLEAN_CLOSE_CODES = new Set<number>([1000, 1001, 1005]);

/**
 * After this many consecutive failed reconnects (i.e. closes without a
 * successful auth_ok in between), emit a single `error` log so on-call
 * sees a clean signal that the session is genuinely down.
 */
export const RECONNECT_FAILURE_ERROR_THRESHOLD = 5;

export type CloseLogLevel = 'silent' | 'warn' | 'error';

/** Reason chosen for the level — useful for assertions and for log messages. */
export type CloseLogReason =
  | 'clean'
  | 'protocol-violation'
  | 'sustained-outage'
  | 'transient';

export interface CloseClassifierInput {
  /** `event.code` from the browser CloseEvent. */
  code: number;
  /** True if the socket completed the auth handshake before closing. */
  wasAuthed: boolean;
  /**
   * How many consecutive close events we've seen without an auth_ok in
   * between. Updated by the caller — this function is pure.
   */
  consecutiveFailedReconnects: number;
  /**
   * Whether the caller has already emitted the sustained-outage error
   * for the *current* outage. When true, this classifier will not
   * recommend `error` for the sustained-outage reason again — prevents
   * spamming once per attempt past the threshold.
   */
  sustainedOutageAlreadyLogged: boolean;
}

export interface CloseClassification {
  level: CloseLogLevel;
  reason: CloseLogReason;
  /** Stable human-readable message keyed off `reason`. */
  message: string;
}

/**
 * Pure function: decide how to log a CloseEvent.
 *
 * Order matters — protocol violations are bugs that must surface even
 * during a sustained outage, and a clean tear-down should win over a
 * stale outage marker (the auth_ok that *would* have reset the counter
 * just hadn't happened yet).
 */
export function classifyClose(
  input: CloseClassifierInput,
): CloseClassification {
  if (PROTOCOL_VIOLATION_CLOSE_CODES.has(input.code)) {
    return {
      level: 'error',
      reason: 'protocol-violation',
      message: 'WebSocket closed: protocol violation',
    };
  }
  if (
    input.consecutiveFailedReconnects >= RECONNECT_FAILURE_ERROR_THRESHOLD &&
    !input.sustainedOutageAlreadyLogged
  ) {
    return {
      level: 'error',
      reason: 'sustained-outage',
      message: 'WebSocket unable to reconnect after repeated failures',
    };
  }
  if (CLEAN_CLOSE_CODES.has(input.code) && input.wasAuthed) {
    return {
      level: 'silent',
      reason: 'clean',
      message: 'WebSocket closed cleanly',
    };
  }
  return {
    level: 'warn',
    reason: 'transient',
    message: 'WebSocket closed unexpectedly',
  };
}
